import { Controller } from "@hotwired/stimulus"

// Draggable 3-slice pie chart; slices always sum to 100% (personal, strategic, tactical).
export default class extends Controller {
  static values = {
    personal: { type: Number, default: 33 },
    strategic: { type: Number, default: 33 },
    tactical: { type: Number, default: 34 }
  }

  static targets = [
    "svg",
    "personalSlice",
    "strategicSlice",
    "tacticalSlice",
    "handleOne",
    "handleTwo",
    "personalPct",
    "strategicPct",
    "tacticalPct",
    "summary",
    "error"
  ]

  connect() {
    this.minPct = 5
    this.cx = 150
    this.cy = 150
    this.radius = 115
    this.handleRadius = 24
    this.startAngle = -Math.PI / 2

    this.normalizeValues()
    requestAnimationFrame(() => {
      this.render()
      this.bindPointerEvents()
      this.dispatchChange()
    })
  }

  disconnect() {
    this.unbindPointerEvents()
  }

  normalizeValues() {
    let p = Math.round(this.personalValue)
    let s = Math.round(this.strategicValue)
    let t = Math.round(this.tacticalValue)
    const total = p + s + t

    if (total !== 100) {
      if (total === 0) {
        p = 34
        s = 33
        t = 33
      } else {
        p = Math.round((p / total) * 100)
        s = Math.round((s / total) * 100)
        t = 100 - p - s
      }
    }

    this.personalValue = this.clamp(p)
    this.strategicValue = this.clamp(s)
    this.tacticalValue = this.clamp(t)
    this.rebalanceIfNeeded()
  }

  clamp(value) {
    return Math.max(this.minPct, Math.min(100 - 2 * this.minPct, value))
  }

  rebalanceIfNeeded() {
    let p = this.personalValue
    let s = this.strategicValue
    let t = this.tacticalValue
    let total = p + s + t

    if (total !== 100) {
      t = 100 - p - s
      if (t < this.minPct) {
        t = this.minPct
        const remainder = 100 - t
        p = Math.max(this.minPct, Math.round(remainder * (p / (p + s))))
        s = 100 - p - t
      }
    }

    this.personalValue = p
    this.strategicValue = s
    this.tacticalValue = t
  }

  get angleOne() {
    return this.startAngle + (this.personalValue / 100) * Math.PI * 2
  }

  get angleTwo() {
    return this.angleOne + (this.strategicValue / 100) * Math.PI * 2
  }

  render() {
    if (!this.hasSvgTarget) return

    const slices = [
      { target: "personalSlice", start: this.startAngle, end: this.angleOne },
      { target: "strategicSlice", start: this.angleOne, end: this.angleTwo },
      { target: "tacticalSlice", start: this.angleTwo, end: this.startAngle + Math.PI * 2 }
    ]

    if (this.hasPersonalSliceTarget) {
      this.personalSliceTarget.setAttribute("d", this.arcPath(this.startAngle, this.angleOne))
    }
    if (this.hasStrategicSliceTarget) {
      this.strategicSliceTarget.setAttribute("d", this.arcPath(this.angleOne, this.angleTwo))
    }
    if (this.hasTacticalSliceTarget) {
      this.tacticalSliceTarget.setAttribute("d", this.arcPath(this.angleTwo, this.startAngle + Math.PI * 2))
    }

    this.positionHandle(this.handleOneTarget, this.angleOne, "personal")
    this.positionHandle(this.handleTwoTarget, this.angleTwo, "strategic")

    if (this.hasPersonalPctTarget) this.personalPctTarget.textContent = `${this.personalValue}%`
    if (this.hasStrategicPctTarget) this.strategicPctTarget.textContent = `${this.strategicValue}%`
    if (this.hasTacticalPctTarget) this.tacticalPctTarget.textContent = `${this.tacticalValue}%`

    this.updateSummary()
  }

  arcPath(startAngle, endAngle) {
    const start = this.polar(this.radius, startAngle)
    const end = this.polar(this.radius, endAngle)
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0

    return [
      `M ${this.cx} ${this.cy}`,
      `L ${start.x} ${start.y}`,
      `A ${this.radius} ${this.radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
      "Z"
    ].join(" ")
  }

  polar(radius, angle) {
    return {
      x: this.cx + radius * Math.cos(angle),
      y: this.cy + radius * Math.sin(angle)
    }
  }

  positionHandle(el, angle, segment) {
    if (!el) return
    const outer = this.polar(this.radius, angle)
    el.setAttribute("cx", outer.x.toFixed(2))
    el.setAttribute("cy", outer.y.toFixed(2))
    el.setAttribute("r", this.handleRadius)
    el.dataset.segment = segment
  }

  bindPointerEvents() {
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)

    if (this.hasSvgTarget) {
      this.svgTarget.addEventListener("pointerdown", this.onPointerDown)
    }
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)
    window.addEventListener("pointercancel", this.onPointerUp)
  }

  unbindPointerEvents() {
    if (this.hasSvgTarget) {
      this.svgTarget.removeEventListener("pointerdown", this.onPointerDown)
    }
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    window.removeEventListener("pointercancel", this.onPointerUp)
  }

  onPointerDown(event) {
    if (!this.hasSvgTarget) return
    const handle = event.target.closest("[data-segment]")
    if (!handle && !this.svgTarget.contains(event.target)) return

    event.preventDefault()
    if (handle) {
      this.activeHandle = handle.dataset.segment === "personal" ? 1 : 2
      handle.setPointerCapture(event.pointerId)
    } else {
      this.activeHandle = this.nearestHandleFromEvent(event)
      this.svgTarget.setPointerCapture(event.pointerId)
    }
    document.body.classList.add("touch-none", "select-none")
  }

  nearestHandleFromEvent(event) {
    const angle = this.pointerAngle(event)
    const distOne = Math.abs(this.normalizeRelative(angle) - this.normalizeRelative(this.angleOne))
    const distTwo = Math.abs(this.normalizeRelative(angle) - this.normalizeRelative(this.angleTwo))
    return distOne <= distTwo ? 1 : 2
  }

  normalizeRelative(angle) {
    let relative = angle - this.startAngle
    while (relative < 0) relative += Math.PI * 2
    while (relative > Math.PI * 2) relative -= Math.PI * 2
    return relative
  }

  onPointerMove(event) {
    if (!this.activeHandle) return
    event.preventDefault()
    this.applyDragAngle(this.pointerAngle(event))
    this.render()
    this.dispatchChange()
  }

  onPointerUp() {
    if (!this.activeHandle) return
    this.activeHandle = null
    document.body.classList.remove("touch-none", "select-none")
  }

  pointerAngle(event) {
    const rect = this.svgTarget.getBoundingClientRect()
    const scaleX = this.svgTarget.viewBox.baseVal.width / rect.width
    const scaleY = this.svgTarget.viewBox.baseVal.height / rect.height
    const x = (event.clientX - rect.left) * scaleX
    const y = (event.clientY - rect.top) * scaleY
    return Math.atan2(y - this.cy, x - this.cx)
  }

  applyDragAngle(angle) {
    const minRadians = (this.minPct / 100) * Math.PI * 2
    const fullCircle = this.startAngle + Math.PI * 2

    if (this.activeHandle === 1) {
      let a1 = this.clampAngle(angle, this.startAngle + minRadians, this.angleTwo - minRadians)
      const personal = ((a1 - this.startAngle) / (Math.PI * 2)) * 100
      this.personalValue = Math.round(personal)
      this.rebalanceFromPersonalStrategic()
    } else {
      let a2 = this.clampAngle(angle, this.angleOne + minRadians, fullCircle - minRadians)
      const strategic = ((a2 - this.angleOne) / (Math.PI * 2)) * 100
      this.strategicValue = Math.round(strategic)
      this.tacticalValue = 100 - this.personalValue - this.strategicValue
      this.rebalanceIfNeeded()
    }
  }

  rebalanceFromPersonalStrategic() {
    const maxStrategicTactical = 100 - this.personalValue - this.minPct
    if (this.strategicValue > maxStrategicTactical) {
      this.strategicValue = maxStrategicTactical
    }
    this.tacticalValue = 100 - this.personalValue - this.strategicValue
    if (this.tacticalValue < this.minPct) {
      this.tacticalValue = this.minPct
      this.strategicValue = 100 - this.personalValue - this.tacticalValue
    }
    this.strategicValue = Math.max(this.minPct, this.strategicValue)
    this.tacticalValue = 100 - this.personalValue - this.strategicValue
  }

  clampAngle(angle, min, max) {
    const normalize = (a) => {
      const start = this.startAngle
      let relative = a - start
      while (relative < 0) relative += Math.PI * 2
      while (relative > Math.PI * 2) relative -= Math.PI * 2
      return relative
    }

    const a = normalize(angle)
    const aMin = normalize(min)
    const aMax = normalize(max)
    const clamped = Math.max(aMin, Math.min(aMax, a))
    return clamped + this.startAngle
  }

  updateSummary() {
    const total = this.personalValue + this.strategicValue + this.tacticalValue
    const valid = total === 100

    if (this.hasSummaryTarget) {
      this.summaryTarget.classList.toggle("bg-primary-50", valid)
      this.summaryTarget.classList.toggle("border-green-200", valid)
      this.summaryTarget.classList.toggle("bg-red-50", !valid)
      this.summaryTarget.classList.toggle("border-red-200", !valid)
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.toggle("hidden", valid)
      if (!valid) {
        this.errorTarget.textContent = `Total must equal 100% (currently ${total}%)`
      }
    }
  }

  dispatchChange() {
    this.dispatch("change", {
      detail: {
        personal: this.personalValue,
        strategic: this.strategicValue,
        tactical: this.tacticalValue
      }
    })
  }
}
